import React, { useCallback } from 'react';

import {
  Form,
  FormDataConsumer,
  Pagination,
  ReferenceManyField,
  required,
  SelectInput,
  useGetOne,
  useNotify,
  usePermissions,
  useRecordContext,
  useResourceContext,
  useUpdate,
} from 'react-admin';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';

import LinkIcon from '@mui/icons-material/Link';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import useDialogStatus from '../../hooks/useDialogStatus';
import { ProductListBody } from './ProductList';
import ReferenceEntityInputV2 from '../../customFields/ReferenceEntityInputV2';
import { IIndividual } from '../../utilities/schemas/individual';
import { IBusiness } from '../../utilities/schemas/business';
import { IAccountEntity } from '../../utilities/schemas/accountEntity';
import entityTypes, {
  EntityTypes,
  KnownEntityTypes,
  entityTypesFromResource,
  resourceFromEntityTypes,
} from '../../constants/entityTypes';
import productPartyRoles, { getProductPartyRolesChoices, ProductPartyRoles } from '../../constants/productPartyRoles';
import hasAccess from '../../utilities/hasAccess';
import actions, { Actions } from '../../constants/actions';
import { IProduct } from '../../utilities/schemas/product';
import useContinuousRefetch from '../../hooks/useContinuousRefetch';

const RemoveConnectedProduct = ({
  parentId,
  parentEntityType,
  refetch,
}: {
  parentId: string;
  parentEntityType: EntityTypes;
  refetch: () => void;
}) => {
  const record = useRecordContext<IProduct>();
  const notify = useNotify();

  const role = record?.parties
    ?.find((party) => party.id === parentId && party.type === parentEntityType)?.role;

  const [removeConnectedProduct] = useUpdate('products', {
    id: record?.id,
    data: {
      removeParties: [{ id: parentId, type: parentEntityType, role }],
    },
  }, {
    onSuccess: () => {
      refetch();
      notify('Connected product removed successfully');
    },
    onError: () => {
      notify('Failed to remove connected product', { type: 'error' });
    },
  });

  const handleRemove = useCallback(() => {
    if (!record) return;

    removeConnectedProduct();
  }, [record, removeConnectedProduct]);

  return (
    <IconButton onClick={handleRemove}>
      <RemoveCircleOutlineIcon color="error" />
    </IconButton>
  );
};

const getRoleFromEntityType = (type: EntityTypes) => {
  if (
    type === entityTypes.INDIVIDUAL
    || type === entityTypes.BUSINESS
  ) return productPartyRoles.PRODUCT_HOLDER;
  if (type === entityTypes.ACCOUNT) return productPartyRoles.CONNECTED_ACCOUNT;
  return null;
};

const SelectProductRole = ({
  productId,
}: {
  productId: string;
}) => {
  const { data: product } = useGetOne<IProduct>('products', { id: productId });
  if (!product) return null;

  const productPartyRolesChoices = getProductPartyRolesChoices(product.productType);
  return (
    <SelectInput
      source="role"
      label="Role of the entity in the product"
      validate={required()}
      choices={productPartyRolesChoices}
    />
  );
};

type FormData = {
  entity?: {
    id: string;
    type: EntityTypes;
  };
  role?: ProductPartyRoles;
}

const ConnectedProducts = () => {
  const { open, openDialog, closeDialog } = useDialogStatus();
  const record = useRecordContext<IIndividual | IBusiness | IAccountEntity>();

  const resource = useResourceContext();

  const { refetchInterval, refetch } = useContinuousRefetch();

  const [update] = useUpdate();

  const handleSubmit = useCallback(async (data: {
    entity?: {
      id: string;
      type: EntityTypes;
    };
    role?: ProductPartyRoles;
  }) => {
    if (!resource || !record?.id || !data.entity || !data.role) return;
    const entityType = entityTypesFromResource[resource];
    await update(resourceFromEntityTypes[data.entity.type as KnownEntityTypes], {
      id: data.entity.id,
      data: {
        addParties: [{ id: record.id, type: entityType, role: data.role }],
      },
    }, {
      onSuccess: () => {
        closeDialog();
        refetch();
      },
    });
  }, [closeDialog, record?.id, refetch, resource, update]);

  const { permissions } = usePermissions<Actions[]>();
  const canUpdateProduct = hasAccess(permissions, actions.PRODUCT_UPDATE);

  if (!record || !resource) return null;

  return (
    <Grid container>
      {canUpdateProduct && (
        <Grid size={12}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              startIcon={<LinkIcon color="secondary" />}
              onClick={openDialog}
            >
              Connect product
            </Button>
            <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="xs">
              <Form
                record={{ role: getRoleFromEntityType(entityTypesFromResource[resource]) }}
                onSubmit={handleSubmit}
              >
                <DialogTitle>Connect product</DialogTitle>
                <DialogContent>
                  <Stack spacing={4}>
                    <ReferenceEntityInputV2 source="entity" label="Product to connect" validate={required()} allowedTypes={[entityTypes.PRODUCT]} />
                    <FormDataConsumer<FormData>>
                      {({ formData }) => (
                        formData.entity && (
                          <SelectProductRole productId={formData.entity.id} />
                        )
                      )}
                    </FormDataConsumer>
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeDialog} color="secondary">
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained">
                    Save
                  </Button>
                </DialogActions>
              </Form>
            </Dialog>
          </Box>
        </Grid>
      )}
      <Grid size={12}>
        <Card variant="outlined" sx={{ marginTop: 1 }}>
          <ReferenceManyField
            fullWidth
            perPage={5}
            pagination={<Pagination rowsPerPageOptions={[5, 10, 20]} />}
            reference="products"
            target="parties.id"
            sort={{ field: 'createdAt', order: 'DESC' }}
            queryOptions={{ refetchInterval }}
          >
            <ProductListBody>
              {canUpdateProduct && (
                <RemoveConnectedProduct
                  parentId={record.id}
                  parentEntityType={entityTypesFromResource[resource]}
                  refetch={refetch}
                />
              )}
            </ProductListBody>
          </ReferenceManyField>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ConnectedProducts;
